import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SuspendedQueryItem } from '../../../models/api/query/query-parameters';
import { Action } from '../../../models/core/action';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToolbarButtonComponent } from '../../core/buttons/toolbar-button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ProgressbarComponent } from '../../core/labels/progressbar.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { QUERY_LIST_PAGE_SIZE_OPTIONS } from '../query-list-page-size-options';

@Component({
  selector: 'app-suspended-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableComponent,
    ColumnComponent,
    ButtonComponent,
    ActionButtonComponent,
    ToolbarButtonComponent,
    SelectComponent,
    CheckboxComponent,
    ActionRowComponent,
    EmptyStateComponent,
    MenuComponent,
    ProgressbarComponent,
  ],
  template: `
    <div>
      @if (retentionNotice) {
        <div
          class="tw-bg-[var(--sed-open-grey-color)] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-3 tw-mb-4 tw-flex tw-items-center tw-gap-2"
        >
          <i class="fa-light fa-circle-info" aria-hidden="true"></i>
          <span>{{ retentionNotice }}</span>
        </div>
      }

      <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2 sm:tw-gap-0 tw-mb-4">
        <app-checkbox
          class="tw-w-full sm:tw-w-1/4 tw-min-w-0"
          [control]="includePublicControl"
          label="Include Public"
        />
        <app-select
          class="tw-w-full sm:tw-w-1/4 tw-min-w-0"
          [control]="lastRunWithinControl"
          label="Last Run Within"
          [options]="dateRangeOptions"
          optionLabel="label"
          optionValue="value"
          [filter]="false"
          [showClear]="false"
        />
        <app-button class="tw-w-full sm:tw-w-1/4 tw-pb-2 tw-self-end" label="Search" icon="fa-light fa-search" (click)="search()" />
      </div>

      <div class="tw-flex tw-flex-row tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-pause"
            label="Loading suspended queries"
            message="Retrieving suspended queries"
            class="tw-w-full"
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else {
          <app-table
            class="tw-w-full"
            [dataSource]="suspendedQueries"
            [showRefresh]="true"
            (refresh)="loadSuspendedQueries(true)"
            [showSelection]="true"
            [selectionKey]="'queryId'"
            [isRowSelectable]="isQuerySelectable"
            [showFilter]="true"
            [sortField]="sortField"
            [sortOrder]="sortOrder"
            recordLabel="Suspended Query"
            recordLabelPlural="Suspended Queries"
            dataKey="queryId"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            [persistPaginationSettings]="true"
            tableId="query-view-suspended"
            (selectedEvent)="onSelectionChange($event)"
          >
        <app-column name="name" label="Name" [sort]="true" />
        <app-column name="description" label="Description" />
        <app-column name="owner" label="Owner" [sort]="true" />
        <app-column name="queryTypeName" label="Type" [sort]="true" />
        <app-column name="lastRun" label="Last Run" [sort]="true">
          <ng-template #cell let-row>
            {{ row.lastRun | date : 'short' }}
          </ng-template>
        </app-column>
        <app-column name="actions" label="Actions">
          <ng-template #cell let-row>
            @if (row.isOwner) {
              <span class="md:tw-hidden">
                <app-action-button
                  [menu]="rowMenu"
                  [row]="row"
                  (click)="openRowMenu($event, row)"
                />
              </span>
            }
            <div class="tw-hidden md:tw-flex tw-gap-1">
              @if (row.isOwner) {
                <app-button
                  label="Reactivate"
                  icon="fa-light fa-rotate-right"
                  emphasis="Low"
                  (click)="reactivateQuery(row)"
                />
                <app-toolbar-button
                  type="Compact"
                  icon="fa-light fa-trash-can"
                  ariaLabel="Delete"
                  (click)="deleteQuery(row)"
                />
              }
            </div>
          </ng-template>
        </app-column>
      </app-table>
        }
      </div>

      <app-menu [items]="rowMenuItems" #rowMenu (click)="onRowAction($event)" />

      @if (selectedQueries.length > 0) {
        <app-action-row class="tw-mt-4">
          <span left1>{{ selectedQueries.length }} selected</span>
          @if (selectedOwnedQueries.length > 0) {
            <app-button
              right1
              label="Reactivate Selected"
              icon="fa-light fa-rotate-right"
              emphasis="Low"
              (click)="reactivateSelected()"
            />
          }
          @if (selectedOwnedQueries.length > 0) {
            <app-button
              right2
              label="Delete Selected"
              icon="fa-light fa-trash-can"
              emphasis="Low"
              (click)="deleteSelected()"
            />
          }
        </app-action-row>
      }
    </div>
  `,
  styles: ``,
})
export class SuspendedListComponent extends BaseComponent implements OnInit {
  @ViewChild('rowMenu')
  rowMenu: MenuComponent;

  suspendedQueries: SuspendedQueryItem[] = [];
  selectedQueries: SuspendedQueryItem[] = [];
  loading = false;
  totalCount = 0;
  pageSize = 10;
  pageSizeOptions = [...QUERY_LIST_PAGE_SIZE_OPTIONS];
  sortField = 'lastRun';
  sortOrder = -1;
  retentionNotice = '';

  isQuerySelectable = (query: SuspendedQueryItem): boolean => query.isOwner;

  get selectedOwnedQueries(): SuspendedQueryItem[] {
    return this.selectedQueries.filter((q) => q.isOwner);
  }

  includePublicControl = new FormControl(false);
  lastRunWithinControl = new FormControl('30d');

  dateRangeOptions = [
    { label: '30 Days', value: '30d' },
    { label: '60 Days', value: '60d' },
    { label: '6 Months', value: '6mo' },
    { label: '1 Year', value: '1yr' },
    { label: 'All', value: 'all' },
  ];

  rowMenuItems: Action[] = [];

  ownerRowActions: Action[] = [
    { label: 'Reactivate', key: 'reactivate' },
    { label: 'Delete', key: 'delete' },
  ];

  async ngOnInit(): Promise<void> {
    this.dispose.subs = this.includePublicControl.valueChanges.subscribe(() => {
      void this.loadSuspendedQueries(false);
    });
    this.dispose.subs = this.lastRunWithinControl.valueChanges.subscribe(() => {
      void this.loadSuspendedQueries(false);
    });

    await this.loadSuspendedQueries(true);
  }

  async loadSuspendedQueries(showLoading = true): Promise<void> {
    if (showLoading) {
      this.loading = true;
    }
    try {
      const response = await this.api.query.getSuspendedQueries({
        lastRunWithin: this.lastRunWithinControl.value ?? '30d',
        includePublic: this.includePublicControl.value ?? false,
        sortField: this.sortField,
        sortDirection: this.sortOrder === -1 ? 'desc' : 'asc',
      });
      this.suspendedQueries = response.items;
      this.totalCount = response.paging.totalCount;
      this.retentionNotice = response.retentionNotice;
      this.selectedQueries = [];
    } finally {
      this.loading = false;
    }
  }

  search(): void {
    void this.loadSuspendedQueries(false);
  }

  onSelectionChange(selected: SuspendedQueryItem[]): void {
    this.selectedQueries = selected;
  }

  reactivateQuery(query: SuspendedQueryItem): void {
    this.confirm.show({
      label: 'Reactivate Query',
      message: `Reactivate query "${query.name}"?`,
      confirm: async () => {
        const response = await this.api.query.reactivateQueries([query.queryId]);
        if (response.success) {
          await this.loadSuspendedQueries();
        }
      },
    });
  }

  openRowMenu(event: Event, row: SuspendedQueryItem): void {
    this.rowMenuItems = row.isOwner
      ? this.ownerRowActions.map((a) => ({ label: a.label, key: a.key }))
      : [];
    this.rowMenu.items = this.rowMenuItems;
    this.rowMenu.toggle(event, row);
  }

  onRowAction(action: Action): void {
    const query = (this.rowMenu?.data ?? action.data) as SuspendedQueryItem;
    if (!query) return;

    switch (action.key) {
      case 'reactivate':
        this.reactivateQuery(query);
        break;
      case 'delete':
        this.deleteQuery(query);
        break;
    }
  }

  deleteQuery(query: SuspendedQueryItem): void {
    this.confirm.show({
      label: 'Delete Suspended Query',
      message: `Delete suspended query "${query.name}"? This action cannot be undone.`,
      confirm: async () => {
        const response = await this.api.query.deleteSuspendedQuery(
          query.queryId,
        );
        if (response.success) {
          await this.loadSuspendedQueries();
        }
      },
    });
  }

  reactivateSelected(): void {
    const count = this.selectedOwnedQueries.length;
    this.confirm.show({
      label: 'Reactivate Queries',
      message: `Reactivate ${count} selected suspended ${count === 1 ? 'query' : 'queries'}?`,
      confirm: async () => {
        const response = await this.api.query.reactivateQueries(
          this.selectedOwnedQueries.map((q) => q.queryId),
        );
        if (response.success) {
          this.selectedQueries = [];
          await this.loadSuspendedQueries();
        }
      },
    });
  }

  deleteSelected(): void {
    const count = this.selectedOwnedQueries.length;
    this.confirm.show({
      label: 'Delete Suspended Queries',
      message: `Delete ${count} selected suspended ${count === 1 ? 'query' : 'queries'}? This action cannot be undone.`,
      confirm: async () => {
        for (const query of this.selectedOwnedQueries) {
          await this.api.query.deleteSuspendedQuery(query.queryId);
        }
        this.selectedQueries = [];
        await this.loadSuspendedQueries();
      },
    });
  }
}
